import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { TextField, DateField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { DriverSelect } from '../../components/@shared/DriverSelect';
import { FacilityGroupSelect } from '../../components/@shared/FacilityGroupSelect';
import { HaulerSelect } from '../../components/@shared/HaulerSelect';
import { TrailerSelect } from '../../components/@shared/TrailerSelect';
import { TruckSelect } from '../../components/@shared/TruckSelect';
import { $routeForm, $updateRouteForm } from '../../state/routeForm.localState';

import { RouteServiceSelector } from './RouteServiceSelector';

export type RouteFormProps = {
  isViewMode?: boolean;
  activeTab?: 'services' | 'routeMap';
};

export const RouteForm = ({
  isViewMode = false,
  activeTab = 'services',
}: RouteFormProps) => {
  const t = useTranslations();
  const form = useAtomValue($routeForm);
  const updateForm = useSetAtom($updateRouteForm);

  return (
    <div className="@RouteForm flex max-h-[380px] gap-8">
      {/* Left: Form fields */}
      <div className="flex w-1/3 flex-col gap-4">
        <FacilityGroupSelect
          label={t.routesView.form.fields.facilityGroup}
          value={form.facilityGroupId}
          onChange={(value) =>
            updateForm({ facilityGroupId: value?.toString() || '' })
          }
          isRequired
          isDisabled={isViewMode}
        />
        <TextField
          label={t.routesView.form.fields.name}
          value={form.description}
          onChange={(value: string) => updateForm({ description: value })}
          placeholder={t.routesView.form.placeholders.name}
          isDisabled={isViewMode}
        />
        <HaulerSelect
          label={t.servicesView.form.fields.hauler}
          value={form.haulerId || ''}
          onChange={(value) =>
            updateForm({
              haulerId:
                value?.toString() === '' ? undefined : value?.toString(),
            })
          }
          onHaulerChange={() => {
            if (form.driverId) {
              updateForm({ driverId: undefined });
            }
          }}
          isDisabled={isViewMode}
        />
        <DriverSelect
          label={t.routesView.form.fields.driver}
          value={form.driverId || ''}
          onChange={(driverId) => updateForm({ driverId: driverId as string })}
          haulerId={form.haulerId}
          isDisabled={isViewMode}
        />
        <TruckSelect
          label={t.servicesView.form.fields.truck}
          value={form.truckId ?? ''}
          onChange={(value) =>
            updateForm({
              truckId: value?.toString() === '' ? undefined : value?.toString(),
            })
          }
          isDisabled={isViewMode}
        />
        <TrailerSelect
          label={t.servicesView.form.fields.trailer}
          value={form.trailerId ?? ''}
          onChange={(value) =>
            updateForm({
              trailerId:
                value?.toString() === '' ? undefined : value?.toString(),
            })
          }
          isDisabled={isViewMode}
        />
        <DateField
          label={t.routesView.form.fields.routeDate}
          value={form.routeDate ?? undefined}
          onChange={(date) => updateForm({ routeDate: date ?? undefined })}
          isDisabled={isViewMode}
        />
      </div>

      {/* Right: Service selector panel */}
      <div className="w-2/3">
        <RouteServiceSelector isViewMode={isViewMode} activeTab={activeTab} />
      </div>
    </div>
  );
};
